// @ts-nocheck
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { generateClient } from "aws-amplify/api";

const client = generateClient();

// Credited author scalars. The matching public profile is resolved client-side (by authorType)
// rather than via a GraphQL join — see ModelGardenModal.
const AUTHOR_FIELDS = /* GraphQL */ `
  authorType
  authorProfileId
`;

// GraphQL queries - will be auto-generated after schema update
const listModelSchemaTemplates = /* GraphQL */ `
  query ListModelSchemaTemplates(
    $filter: ModelModelSchemaTemplateFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listModelSchemaTemplates(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        category
        description
        requiresTraining
        trainingCost
        version
        executionUrl
        fieldDefinitions
        modelKind
        historicalConfig
        external
        isActive
        tags
        profilePictureUrl
        createdBy
        ${AUTHOR_FIELDS}
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const getModelSchemaTemplate = /* GraphQL */ `
  query GetModelSchemaTemplate($id: ID!) {
    getModelSchemaTemplate(id: $id) {
      id
      name
      category
      description
      requiresTraining
      trainingCost
      version
      executionUrl
      fieldDefinitions
      modelKind
      historicalConfig
      external
      isActive
      tags
      profilePictureUrl
      createdBy
      ${AUTHOR_FIELDS}
      createdAt
      updatedAt
    }
  }
`;

const createModelSchemaTemplate = /* GraphQL */ `
  mutation CreateModelSchemaTemplate(
    $input: CreateModelSchemaTemplateInput!
  ) {
    createModelSchemaTemplate(input: $input) {
      id
      name
      category
      description
      requiresTraining
      trainingCost
      version
      executionUrl
      fieldDefinitions
      modelKind
      historicalConfig
      external
      isActive
      tags
      profilePictureUrl
      createdBy
      ${AUTHOR_FIELDS}
      createdAt
      updatedAt
    }
  }
`;

const updateModelSchemaTemplate = /* GraphQL */ `
  mutation UpdateModelSchemaTemplate(
    $input: UpdateModelSchemaTemplateInput!
  ) {
    updateModelSchemaTemplate(input: $input) {
      id
      name
      category
      description
      requiresTraining
      trainingCost
      version
      executionUrl
      fieldDefinitions
      modelKind
      historicalConfig
      external
      isActive
      tags
      profilePictureUrl
      createdBy
      ${AUTHOR_FIELDS}
      createdAt
      updatedAt
    }
  }
`;

const deleteModelSchemaTemplate = /* GraphQL */ `
  mutation DeleteModelSchemaTemplate(
    $input: DeleteModelSchemaTemplateInput!
  ) {
    deleteModelSchemaTemplate(input: $input) {
      id
    }
  }
`;

const initialState = {
  loading: "idle",
  error: null,
  templates: {},
  templateList: [],
};

// Matches the Lambda-side catalog paging (mcpProxyFunction systemPrompt.js) so both surfaces see
// the same templates; the cap keeps a runaway table from stalling the app.
const TEMPLATE_FETCH_PAGE_LIMIT = 100;
const TEMPLATE_FETCH_MAX_PAGES = 5;

export const fetchModelSchemaTemplates = createAsyncThunk(
  "modelSchemaTemplate/fetchAll",
  async (_, thunkAPI) => {
    try {
      // No isActive filter here: the admin Model Garden needs inactive templates
      // listed so they can be re-activated. User-facing surfaces (ModelGardenModal)
      // filter to isActive === true client-side.
      //
      // Paginated: AppSync caps a page at 100 items, so a single call silently dropped templates
      // past that — which then read as "unavailable" on any surface that looks one up by id.
      const items = [];
      let nextToken = null;
      for (let page = 0; page < TEMPLATE_FETCH_MAX_PAGES; page += 1) {
        const { data } = await client.graphql({
          query: listModelSchemaTemplates,
          variables: { limit: TEMPLATE_FETCH_PAGE_LIMIT, nextToken },
        });
        const pageData = data?.listModelSchemaTemplates;
        items.push(...(pageData?.items || []));
        nextToken = pageData?.nextToken || null;
        if (!nextToken) break;
      }
      if (nextToken) {
        console.warn(`Model template list truncated at ${items.length} templates (page cap reached)`);
      }
      const templateDict = items.reduce(
        (acc, curr) => ({ ...acc, [curr.id]: curr }),
        {}
      );
      return {
        dict: templateDict,
        list: items
      };
    } catch (error) {
      console.error("Error fetching model schema templates:", error);
      return thunkAPI.rejectWithValue(error.message || "Failed to fetch templates");
    }
  }
);

export const fetchModelSchemaTemplateById = createAsyncThunk(
  "modelSchemaTemplate/fetchById",
  async (id, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: getModelSchemaTemplate,
        variables: { id },
      });
      return data.getModelSchemaTemplate;
    } catch (error) {
      console.error("Error fetching model schema template:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const postModelSchemaTemplate = createAsyncThunk(
  "modelSchemaTemplate/post",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: createModelSchemaTemplate,
        variables: { input },
      });
      return data.createModelSchemaTemplate;
    } catch (error) {
      console.error("Error creating model schema template:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const editModelSchemaTemplate = createAsyncThunk(
  "modelSchemaTemplate/edit",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: updateModelSchemaTemplate,
        variables: { input },
      });
      return data.updateModelSchemaTemplate;
    } catch (error) {
      console.error("Error updating model schema template:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Flips only isActive. Separate from editModelSchemaTemplate so the list's global
// loading state isn't touched — the admin list stays rendered while the switch saves.
export const toggleModelSchemaTemplateActive = createAsyncThunk(
  "modelSchemaTemplate/toggleActive",
  async ({ id, isActive }, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: updateModelSchemaTemplate,
        variables: { input: { id, isActive } },
      });
      return data.updateModelSchemaTemplate;
    } catch (error) {
      console.error("Error toggling model schema template active state:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteModelSchemaTemplateFunc = createAsyncThunk(
  "modelSchemaTemplate/delete",
  async (input, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: deleteModelSchemaTemplate,
        variables: { input },
      });
      return data.deleteModelSchemaTemplate;
    } catch (error) {
      console.error("Error deleting model schema template:", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const ModelSchemaTemplateSlice = createSlice({
  name: "modelSchemaTemplates",
  initialState,
  reducers: {
    clearModelSchemaTemplates: (state) => {
      state.templates = {};
      state.templateList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all templates
      .addCase(fetchModelSchemaTemplates.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchModelSchemaTemplates.fulfilled, (state, action) => {
        state.loading = "success";
        state.templates = action.payload.dict;
        state.templateList = action.payload.list;
      })
      .addCase(fetchModelSchemaTemplates.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload || action.error?.message;
      })
      // Fetch by ID
      .addCase(fetchModelSchemaTemplateById.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchModelSchemaTemplateById.fulfilled, (state, action) => {
        state.templates[action.payload.id] = action.payload;
        state.loading = "success";
      })
      .addCase(fetchModelSchemaTemplateById.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      })
      // Create template
      .addCase(postModelSchemaTemplate.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(postModelSchemaTemplate.fulfilled, (state, action) => {
        state.loading = "success";
        state.templates[action.payload.id] = action.payload;
        state.templateList.push(action.payload);
      })
      .addCase(postModelSchemaTemplate.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      })
      // Edit template
      .addCase(editModelSchemaTemplate.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(editModelSchemaTemplate.fulfilled, (state, action) => {
        state.templates[action.payload.id] = action.payload;
        const index = state.templateList.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.templateList[index] = action.payload;
        }
        state.loading = "success";
      })
      .addCase(editModelSchemaTemplate.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      })
      // Toggle active (no loading-state changes; see thunk comment)
      .addCase(toggleModelSchemaTemplateActive.fulfilled, (state, action) => {
        state.templates[action.payload.id] = action.payload;
        const index = state.templateList.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.templateList[index] = action.payload;
        }
      })
      .addCase(toggleModelSchemaTemplateActive.rejected, (state, action) => {
        state.error = action.payload || action.error?.message;
      })
      // Delete template
      .addCase(deleteModelSchemaTemplateFunc.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(deleteModelSchemaTemplateFunc.fulfilled, (state, action) => {
        delete state.templates[action.payload.id];
        state.templateList = state.templateList.filter(t => t.id !== action.payload.id);
        state.loading = "success";
      })
      .addCase(deleteModelSchemaTemplateFunc.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      });
  },
});

export const { clearModelSchemaTemplates } = ModelSchemaTemplateSlice.actions;
export default ModelSchemaTemplateSlice.reducer;
